import { useState } from "react";
import { Alert } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import type { IEnrichedBusiness } from "@/business/business";
import { extractErrorMessage } from "@/core/api/errors";
import { OnboardingChecklist } from "@/onboarding/simple-onboarding/onboarding-checklist";
import { useOnboardingState } from "@/onboarding/simple-onboarding/onboarding-state-context";
import {
  firstOpenTask,
  onboardingTasks,
  tasksDone,
} from "@/onboarding/simple-onboarding/onboarding-tasks";
import { useOnboardingNavigation } from "@/onboarding/simple-onboarding/shared-query";
import type { IntakeStep } from "@/onboarding/simple-onboarding/types";

interface WelcomeScreenProps {
  business: Pick<IEnrichedBusiness, "id" | "name">;
}

/**
 * The whole checklist, up front. A customer who can see every task before starting can
 * line up whoever else has to be involved, which is most of them: the person who runs
 * the mail, the one who administers ServiceTitan, the one who keeps the old invoices.
 */
export const WelcomeScreen = ({ business }: WelcomeScreenProps) => {
  const state = useOnboardingState();
  const navigation = useOnboardingNavigation(business.id);
  const [error, setError] = useState<string>();
  const tasks = state ? onboardingTasks(state) : [];
  const done = tasksDone(tasks);
  const ap = Boolean(state?.steps.includes("invoice_sources"));

  const goTo = (step: IntakeStep) => {
    setError(undefined);
    navigation.mutate(
      { step: "welcome", direction: "jump", to: step },
      { onError: (failure) => setError(extractErrorMessage(failure).message) },
    );
  };

  const resumeLabel =
    done === 0
      ? "Get started"
      : done === tasks.length
        ? "Review your answers"
        : "Continue where you left off";

  return (
    <Stack
      gap={4}
      align="center"
      className="min-h-[calc(100vh-var(--app-header-height))] w-full bg-surface-sunken px-4 py-10"
    >
      <Stack gap={8} align="stretch" className="w-full max-w-3xl">
        <Stack className="w-full" align="start" gap={1}>
          <Text weight="semibold" variant="caption" tone="brand">
            Welcome to Volca
          </Text>
          <Text variant="display">
            {ap
              ? "Send your information for setup"
              : `Let's set up ${business.name}`}
          </Text>
          <Text tone="muted">
            {ap
              ? `Complete these ${tasks.length} steps, then review and submit your information. Volca will use it to set up your selected products.`
              : `These ${tasks.length} steps tell us what we need to run payroll for you.`}
          </Text>
        </Stack>

        {!!tasks.length && state && (
          <OnboardingChecklist
            tasks={tasks}
            currentStep="welcome"
            onPick={goTo}
            disabled={navigation.isPending}
          />
        )}

        {error && <Alert color="danger">{error}</Alert>}

        <Stack direction="row" gap={4} align="center" className="flex-wrap">
          <Button
            onClick={() => {
              const next = firstOpenTask(tasks);
              if (next) goTo(next.step);
            }}
            disabled={!tasks.length || navigation.isPending}
            aria-busy={navigation.isPending || undefined}
          >
            {navigation.isPending && (
              <Spinner aria-hidden data-icon="inline-start" />
            )}
            {resumeLabel}
          </Button>
          <Text tone="muted">
            You can do these in any order, and come back to this list at any
            time.
          </Text>
        </Stack>
      </Stack>
    </Stack>
  );
};
